import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackHeapEvent } from 'utils/analytics';
import SetupService1 from './Step1';
import SetupService2 from './Step2';
import SetupService3, { IStep3Ref } from './Step3';
import SetupService4 from './Step4';
import { Button, message } from 'antd';
import s from './s.module.less';
import { getNextStep, getPrevStep } from 'pages/PPAOnboardingSteps/utils';
import { EPPAStep } from 'types/ppaOnboarding';
import { saveKlarityServiceSpecialty, savePPAPaymentMethod, saveStep } from 'api/ppaOnboarding';
import { saveProviderKlarityService } from 'api/klarityService';
import { IReviewData } from 'hooks/usePPAOnboardingReviewInfo';
import useGetPPAServiceDetail from 'hooks/useGetPPAServiceDetail';

interface IProps {
    setShowProgress: (show: boolean) => void;
    step?: EPPAStep;
    setStep: (step: EPPAStep) => void;
    reviewData: IReviewData;
    refetchReviewData?: () => void;
}

const formIds = {
    [EPPAStep.STEP_6]: 'specialty',
    [EPPAStep.STEP_7]: 'payment',
    [EPPAStep.STEP_8]: 'serviceTemplate',
};

const SetupService = ({ setShowProgress, step, setStep, reviewData, refetchReviewData }: IProps) => {
    const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
    const [formId, setFormId] = useState<string>();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [serviceDetail] = useGetPPAServiceDetail();
    const step3Ref = useRef<IStep3Ref>(null);
    const [paymentSelected, setPaymentSelected] = useState<string>('');

    // Seed the payment selection from saved data so the footer "Next" works on a pre-filled answer.
    useEffect(() => {
        if (serviceDetail?.supportPayType) {
            setPaymentSelected((prev) => prev || serviceDetail.supportPayType || '');
        }
    }, [serviceDetail?.supportPayType]);

    useEffect(() => {
        setFormId(formIds[step as keyof typeof formIds]);
    }, [step]);

    useEffect(() => {
        setShowProgress(true);
    }, []);

    const handleBack = useCallback(() => {
        if (step) {
            trackHeapEvent('WebApp - PPA Onboarding - SetupService - Click - Back', {
                step,
            });
            const newStep = getPrevStep(step);
            setStep(newStep);
        } else {
            console.error('step is undefined');
        }
    }, [setStep, step]);

    const handleSaveStep = useCallback(async () => {
        if (step) {
            try {
                setSubmitLoading(true);
                const nextStep = getNextStep(step as EPPAStep);
                const res = await saveStep(nextStep);
                if (res && !res.error) {
                    trackHeapEvent('WebApp - PPA Onboarding - SetupService - Click - Next Success', {
                        fromStep: step,
                        toStep: nextStep,
                    });
                    setStep(nextStep);
                    refetchReviewData?.();
                } else {
                    trackHeapEvent('WebApp - PPA Onboarding - SetupService - View - Next Failed', {
                        step,
                        error: res?.error,
                    });
                    message.error(res?.error);
                }
            } catch (error) {
                trackHeapEvent('WebApp - PPA Onboarding - SetupService - View - Next Error', {
                    step,
                    error: error instanceof Error ? error.message : 'Unknown error',
                });
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        } else {
            console.error('step is undefined');
        }
    }, [setStep, step]);

    const handleNext = useCallback(
        async (data: any, stepVal: EPPAStep) => {
            try {
                setSubmitLoading(true);
                trackHeapEvent('WebApp - PPA Onboarding - SetupService - Click - Next Attempt', {
                    step: stepVal,
                });
                let res = null;
                if (stepVal === EPPAStep.STEP_6) {
                    res = await saveKlarityServiceSpecialty(data);
                } else if (stepVal === EPPAStep.STEP_7) {
                    res = await savePPAPaymentMethod(data.supportPayType);
                } else {
                    res = await saveProviderKlarityService(
                        data.providerKlarityService,
                        data.disableIds || [],
                        data.conditions || [],
                    );
                }
                if (res && !res.error) {
                    trackHeapEvent('WebApp - PPA Onboarding - SetupService - View - Next Success', {
                        step: stepVal,
                    });
                    handleSaveStep();
                } else {
                    trackHeapEvent('WebApp - PPA Onboarding - SetupService - View - Next Failed', {
                        step: stepVal,
                        error: res?.error,
                    });
                    message.error(res?.error);
                }
            } catch (error) {
                trackHeapEvent('WebApp - PPA Onboarding - SetupService - View - Next Error', {
                    step: stepVal,
                    error: error instanceof Error ? error.message : 'Unknown error',
                });
                console.error(error);
            } finally {
                setSubmitLoading(false);
            }
        },
        [handleSaveStep, setSubmitLoading],
    );

    const commonProps = useMemo(() => {
        return {
            currentStep: step,
            setStep,
            setShowProgress,
            setNextButtonDisabled,
            setSubmitLoading,
            onNext: handleNext,
            reviewData,
        };
    }, [handleNext, setShowProgress, setStep, step]);

    const contentRender = useMemo(() => {
        let content = null;

        if (step === EPPAStep.STEP_5) {
            content = <SetupService1 />;
        } else if (step === EPPAStep.STEP_6) {
            content = <SetupService2 serviceDetail={serviceDetail} {...commonProps} formId={formId as string} />;
        } else if (step === EPPAStep.STEP_7) {
            content = (
                <SetupService3
                    ref={step3Ref}
                    serviceDetail={serviceDetail}
                    onNext={handleNext}
                    onSelectionChange={setPaymentSelected}
                />
            );
        } else if (step === EPPAStep.STEP_8) {
            content = <SetupService4 serviceDetail={serviceDetail} onNext={handleNext} formId={formId as string} />;
        }

        return content;
    }, [commonProps, step, formId, handleNext, serviceDetail]);

    const footerBtnsRender = useMemo(() => {
        if (step === EPPAStep.STEP_5) {
            return (
                <>
                    <Button
                        onClick={() => {
                            setStep(getPrevStep(step as EPPAStep));
                        }}
                    >
                        Back
                    </Button>
                    <Button type="primary" onClick={() => handleSaveStep()}>
                        Next
                    </Button>
                </>
            );
        } else if (step === EPPAStep.STEP_7) {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button
                        loading={submitLoading}
                        type="primary"
                        disabled={!paymentSelected}
                        onClick={() => step3Ref.current?.submit()}
                    >
                        Next
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button onClick={handleBack}>Back</Button>
                    <Button
                        loading={submitLoading}
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        disabled={nextButtonDisabled}
                    >
                        Next
                    </Button>
                </>
            );
        }
    }, [step, setStep, handleSaveStep, handleBack, submitLoading, formId, nextButtonDisabled, paymentSelected]);
    return (
        <div className={s.wrap}>
            {contentRender}
            <div className={s.footer}>{footerBtnsRender}</div>
        </div>
    );
};

export default SetupService;
